import isObject from 'lodash/isObject'
import isNumber from 'lodash/isNumber'
import Vector2 from '@antv/f2/lib/graphic/util/vector2'
import Rect from '@antv/f2/lib/graphic/shape/rect'

const KEY = 'elasticController'
const FIELD_KEY = '__field__elastic_painter_plugin'

import ElasticController from './ElasticController'
/**
 * 原始的 coord convert point 方法
 */
const originConvertPoint = (point, x, y, transposed = false) => {
  const xDim = transposed ? 'y' : 'x'
  const yDim = transposed ? 'x' : 'y'
  return {
    x: x.start + ((x.end - x.start) * point[xDim]),
    y: y.start + ((y.end - y.start) * point[yDim]),
  }
}

/**
 * 获取坐标轴基本位置配置
 * @param x
 * @param y
 * @param transposed
 * @param dimType
 * @param position
 * @return {{start: *, end: *, offsetFactor: *}}
 */
const getBasicPlot = ({ x, y, transposed }, dimType, position) => {
  let start
  let end
  let factor = 1

  if (dimType === 'x') {
    start = {
      x: 0,
      y: 0
    }
    end = {
      x: 1,
      y: 0
    }
  } else if (position === 'right') {
    // there will be several y axes
    start = {
      x: 1,
      y: 0
    }
    end = {
      x: 1,
      y: 1
    }
  } else {
    start = {
      x: 0,
      y: 0
    }
    end = {
      x: 0,
      y: 1
    }
    factor = -1
  }

  if (transposed) {
    factor *= -1
  }

  return {
    offsetFactor: factor,
    start: originConvertPoint(start, x, y, transposed),
    end: originConvertPoint(end, x, y, transposed)
  }
}

/**
 * 获取 tick 初始位置
 * @param plot
 * @param value
 * @return {{x: number, y: number}}
 */
const getOffsetPoint = (plot, value) => {
  const { start, end } = plot

  return {
    x: start.x + ((end.x - start.x) * value),
    y: start.y + ((end.y - start.y) * value)
  }
}

/**
 * 获取 tick 终止位置
 * @param plot
 * @param point
 * @param offset
 * @return {{x: *, y: *}}
 */
const getSidePoint = (plot, point, offset) => {
  const { start, end, offsetFactor } = plot
  const axisVector = [end.x - start.x, end.y - start.y]
  const normal = Vector2.normalize([], axisVector)
  const verticalVector = [normal[1] * -1 * offsetFactor, normal[0] * offsetFactor]
  const offsetVector = Vector2.scale([], verticalVector, offset)

  return {
    x: point.x + offsetVector[0],
    y: point.y + offsetVector[1]
  }
}

const formatTicks = (ticks) => {
  const tmp = ticks.slice(0)

  if (tmp.length > 0) {
    const first = tmp[0]
    const last = tmp[tmp.length - 1]

    if (first.value !== 0) {
      tmp.unshift({
        value: 0
      })
    }

    if (last.value !== 1) {
      tmp.push({
        value: 1
      })
    }
  }

  return tmp
}

/**
 * 获取图形的容器
 * @param axis
 * @param isTop
 * @return {*}
 */
const getContainer = (axis, isTop) => {
  const { frontContainer, backContainer } = axis
  return isTop ? frontContainer : backContainer
}

/**
 * 获取线条
 * @param axis
 * @return {null|T}
 */
const getLineShape = (axis) => {
  const { line } = axis
  if (!line) return null

  const container = getContainer(axis, line.top)
  const children = container.getChildren()
  if (!Array.isArray(children)) return null
  return children.find(d => d.get('className') === 'axis-line' && d.get('attrs')[FIELD_KEY] === axis[FIELD_KEY])
}
/**
 * 获取 ticks shape
 * @param axis
 * @return {T[]}
 */
const getTickShapes = (axis) => {
  const { tickLine, _id } = axis
  if (!tickLine) return null

  const container = getContainer(axis, tickLine.top)
  const children = container.getChildren()
  if (!Array.isArray(children)) return null
  return children.filter(d => d._id && d._id.includes(`${_id}`) && d.get('className') === 'axis-tick')
}

/**
 * 获取 label shape
 * @param axis
 * @return {shape[]}
 */
const getLabelShapes = (axis) => {
  const { label, _id } = axis
  if (!label) return null

  const container = getContainer(axis, label.top)
  const children = container.getChildren()
  return children.filter(d => d._id && d._id.includes(`${_id}`) && d.get('className') === 'axis-label')
}

/**
 * 获取 grid shape
 * @param axis
 * @return {T[]}
 */
const getGridShape = (axis) => {
  const { grid, gridPoints, _id } = axis
  if (!grid || !Array.isArray(gridPoints)) return null

  const container = getContainer(axis, grid.top)
  const children = container.getChildren()
  return children.filter(d => d._id && d._id.includes(`${_id}`) && d.get('className') === 'axis-grid')
}

/**
 * 计算图形是否显示或隐藏
 * @param axis
 * @param shape
 * @param x
 * @param y
 * @param plot
 * @param transposed
 * @param caches
 */
const updateShapeState = (axis, shape, x, y, plot, transposed = false, caches = {}) => {
  const { start, end } = plot
  const { dimType } = axis
  const shouldVisible = caches[shape._id]

  if ((dimType === 'x' && !transposed) || (dimType !== 'x' && transposed)) {
    if (x >= start.x && x <= end.x && shouldVisible !== false) {
      shape.show()
    } else {
      shape.hide()
    }
  } else if (y <= start.y && y >= end.y && shouldVisible !== false) {
    shape.show()
  } else {
    shape.hide()
  }
}

const getUpdateAttr = (dimType, transposed) => {
  if (transposed) {
    return dimType
  }
  return dimType === 'x' ? 'y' : 'x'
}
/**
 * 重置 axis line 位置
 * @param axis
 * @param plot
 */
const resetAxisLine = (axis, plot) => {
  const line = getLineShape(axis)
  if (!line) return null

  const { start, end } = plot
  line.attr({
    x1: start.x,
    y1: start.y,
    x2: end.x,
    y2: end.y
  })

  return line.getBBox()
}
/**
 * 重置 axis tick 位置
 * @param axis
 * @param plot
 * @param type
 * @param transposed
 */
const resetAxisTick = (axis, plot, type, transposed) => {
  const { dimType } = axis

  const tickShapes = getTickShapes(axis)
  const { ticks, tickLine } = axis
  if (!Array.isArray(ticks) || !Array.isArray(tickShapes)) return

  const targetAttr = getUpdateAttr(dimType, transposed)
  const updateAttr1 = `${targetAttr}1`
  const updateAttr2 = `${targetAttr}2`

  ticks.forEach((tick, index) => {
    const start = getOffsetPoint(plot, tick.value)
    const end = getSidePoint(plot, start, (tickLine && tickLine.length) || 0)
    const tickShape = tickShapes[index]
    if (tickShape) {
      if (type.includes(targetAttr)) {
        tickShape.attr({
          [updateAttr1]: start[targetAttr],
          [updateAttr2]: end[targetAttr],
        })
      }
      const { x1, y1 } = tickShape.get('attrs')
      updateShapeState(axis, tickShape, x1, y1, plot, transposed)
    }
  })
}

const calculateLabelShow = (label, labels, dimType, transposed, caches) => {
  const direct = transposed ? (dimType === 'x' ? 'y' : 'x') : dimType
  const attrName = direct === 'y' ? 'height' : 'width'

  const attrs = label.get('attrs')
  const position = direct === 'y' ? attrs.y : attrs.x

  let shouldShow = true
  const index = labels.indexOf(label)
  // 第一个不需要检查前面是否被覆盖
  if (index > 0) {
    const size = label.getBBox()[attrName]

    let preIndex = index
    let preLabel
    let prevVisible

    do {
      preIndex--
      preLabel = labels[preIndex]
      prevVisible = caches[preLabel._id]

      const preAttrs = preLabel.get('attrs')
      const prePosition = direct === 'y' ? preAttrs.y : preAttrs.x
      const box = preLabel.getBBox()

      shouldShow = !prevVisible || ((position - (size / 2)) - (prePosition + (box[attrName] / 2))) >= 2

      // if (!shouldShow && index === labels.length - 1) {
      //   shouldShow = true
      //   caches[preLabel._id] = false
      //   preLabel.hide() // 此时这个 label 已经迭代完 需要隐藏
      // }
    } while (preIndex >= 1 && shouldShow)
  }
  caches[label._id] = shouldShow
}

/**
 * 重置 axis label 位置
 * @param axis
 * @param plot
 * @param type
 * @param transposed
 * @param reset
 * @param caches
 */
const resetAxisLabel = (axis, plot, type, transposed, reset = false, caches = {}) => {
  const labelShapes = getLabelShapes(axis)
  if (!Array.isArray(labelShapes)) return

  const { labelOffset, dimType } = axis

  const updateAttr = getUpdateAttr(dimType, transposed)
  // y 轴需要反过来计算 从底部到顶部
  const reverseOrder = (transposed && dimType === 'x') || (!transposed && dimType === 'y')
  const sorted = [...labelShapes].sort((a, b) => (reverseOrder ? -1 : 1) * (a.get('value') - b.get('value')))

  sorted.forEach((labelShape) => {
    const start = getOffsetPoint(plot, labelShape.get('value'))
    const point = getSidePoint(plot, start, labelOffset)
    if (type.includes(updateAttr)) {
      labelShape.attr({
        [updateAttr]: point[updateAttr],
      })
    }

    // 需要调整位置
    if (reset) {
      calculateLabelShow(labelShape, sorted, dimType, transposed, caches)
    }
    const { x, y } = labelShape.get('attrs')
    updateShapeState(axis, labelShape, x, y, plot, transposed, caches)
  })
}
/**
 * 重置 axis grid 位置
 * @param axis
 * @param x
 * @param y
 * @param transposed
 * @param plot
 * @param type
 */
const resetAxisGrid = (axis, { x, y, transposed }, type, plot) => {
  const { ticks, dimType, verticalScale } = axis

  const gridShapes = getGridShape(axis)
  if (!Array.isArray(gridShapes)) return

  if (!verticalScale || !Array.isArray(ticks)) return
  const verticalTicks = formatTicks(verticalScale.getTicks())

  const updateAttr = getUpdateAttr(dimType, transposed)
  const attr2 = updateAttr === 'x' ? 'y' : 'x'

  ticks.forEach((tick, index) => {
    const subPoints = []
    const shape = gridShapes[index]
    if (!shape) return

    const { points } = shape.get('attrs') || {}

    if (type.includes(updateAttr)) {
      verticalTicks.forEach((verticalTick, j) => {
        const xValue = dimType === 'x' ? tick.value : verticalTick.value
        const yValue = dimType === 'x' ? verticalTick.value : tick.value

        if (xValue >= 0 && xValue <= 1 && yValue >= 0 && yValue <= 1) {
          const point = originConvertPoint({ x: xValue, y: yValue }, x, y, transposed)
          const originPoint = points && points[j]
          subPoints.push({
            [updateAttr]: point[updateAttr],
            [attr2]: originPoint ? originPoint[attr2] : point[attr2]
          })
        }
      })

      shape.attr({
        points: subPoints,
      })
    }

    // 确认是否显示
    const { points: newPoints } = shape.get('attrs') || {}
    if (!Array.isArray(newPoints) || newPoints.length <= 0) {
      shape.hide()
    } else {
      updateShapeState(axis, shape, newPoints[0].x, newPoints[0].y, plot, transposed)
    }
  })
}
/**
 * 获取画布剪切蒙版
 * @returns {Rect}
 * @param chart
 * @param axisLinePlots
 * @param margin
 */
const getClip = (chart, axisLinePlots, margin) =>  {
  const coord = chart.get('coord')
  const { start, end } = coord
  const width = end.x - start.x
  const height = Math.abs(end.y - start.y)

  const [topMargin, rightMargin, bottomMargin, leftMargin] = margin

  let xOffset = 0
  let xDistance = 0
  let yDistance = 0

  Object.entries(axisLinePlots).forEach(([position, bbox]) => {
    if (!isObject(bbox) || !isNumber(bbox.width) || !isNumber(bbox.height)) return
    const { width: w, height: h } = bbox
    switch (position) {
      case 'left':
        xOffset += w
        xDistance += w
        break
      case 'right':
        xDistance += w
        break
      case 'bottom':
        yDistance += h
        break
      default:
    }
  })

  const clip = new Rect({
    attrs: {
      x: start.x + (xOffset / 2) - leftMargin,
      y: end.y - topMargin,
      width: width - (xDistance / 2) + (leftMargin + rightMargin),
      height: height - (yDistance / 2) + (topMargin + bottomMargin),
    }
  })

  clip.isClip = true
  return clip
}

/**
 * 复制剪切蒙版
 * @param clip
 * @returns {Rect}
 */
const cloneClip = (clip) => {
  const { x, y, width, height } = clip.get('attrs')
  const newClip = new Rect({
    attrs: {
      x,
      y,
      width,
      height,
    }
  })
  newClip.isClip = true
  return newClip
}

/**
 * 重置轴标题
 * @param chart
 */
// const resetAxisTitleGuide = (chart) => {
//   const guideController = chart.get('guideController')
//   const coord = chart.get('coord')
//   const { x, y, transposed } = coord
//   if (guideController && Array.isArray(guideController.guides)) {
//     const { guides } = guideController
//     guides.forEach((guide) => {
//       if (guide.type === 'text' && guide.resetPosition && guide.element) {
//         const { offsetX, offsetY } = guide
//         const { x: xPoint, y: yPoint } = guide.element.get('attrs')
//         const originX = xPoint - offsetX
//         const originY = yPoint - offsetY
//         const originPosition = originConvertPoint(coord.invertPoint({ x: originX, y: originY }), x, y, transposed)
//         guide.element.attr({
//           x: originPosition.x + offsetX,
//           y: originPosition.y + offsetY
//         })
//       }
//     })
//   }
// }
/**
 * 添加剪切蒙版
 * @param chart
 * @param axisLinePlot
 * @param margin
 */
const updateGeomClip = (chart, axisLinePlot, margin) => {
  const middlePlot = chart.get('middlePlot')
  const clip = getClip(chart, axisLinePlot, margin)

  if (middlePlot) {
    clip.set('canvas', middlePlot.get('canvas'))
    middlePlot.attr('clip', clip)
  }

  // resetAxisTitleGuide(chart)
  // geom clip 移除，y 轴 title 需要显示
  // const frontPlot = chart.get('frontPlot')
  // const backPlot = chart.get('backPlot')
  // let guideContainer = frontPlot ? frontPlot.getChildren() : []
  // guideContainer = guideContainer.concat(backPlot ? backPlot.getChildren() :  [])
  //   .filter(d => d.get('className') === 'guideContainer')

  // guideContainer.forEach((group) =>  {
  //   if (group.attr('clip')) return
  //
  //   const clipShape = cloneClip(clip)
  //   clipShape.set('canvas', group.get('canvas'))
  //   group.attr('clip', clipShape)
  // })

  // tooltip clip
  const { tooltip } = chart.get('tooltipController') || {}
  const { frontPlot: tooltipFront, backPlot: tooltipBack } = tooltip || {}
  if (tooltipFront && !tooltipFront.attr('clip')) {
    const clipShape = cloneClip(clip)
    clipShape.set('canvas', tooltipFront.get('canvas'))
    tooltipFront.attr('clip', clipShape)
  }
  if (tooltipBack && !tooltipBack.attr('clip')) {
    const clipShape = cloneClip(clip)
    clipShape.set('canvas', tooltipBack.get('canvas'))
    tooltipBack.attr('clip', clipShape)
  }
}

/**
 * 更新 axis, 需要重置 line, 确认 label/tick/grid 是否要更新/显示
 */
const updateAxes = (chart, axes, labelCaches, { x, y }, transposed = false, type = 'x', resetLabel = false) => {
  const linePlot = {}

  axes.forEach((axis) => {
    const { showAllLabel } = axis

    const { position, dimType } = axis
    const plot = getBasicPlot({ x, y, transposed }, dimType, position)
    linePlot[position] = resetAxisLine(axis, plot)
    resetAxisGrid(axis, { x, y, transposed }, type, plot)
    resetAxisLabel(axis, plot, type, transposed, resetLabel && !showAllLabel, labelCaches)
    resetAxisTick(axis, plot, type, transposed)
  })

  const elasticController = chart.get(KEY)

  updateGeomClip(chart, linePlot, elasticController.getClipMargin())
}

export default {
  init(chart) {
    const controller = chart.get(KEY)
    if (controller) {
      controller.destroy()
    }
    const elasticController = new ElasticController(chart)
    chart.set(KEY, elasticController)

    // 开启
    chart.enableElasticPainter = (cfg) => {
      const elastic = chart.get(KEY)
      if (elastic) {
        elastic.enable(cfg)
      }
    }

    chart.resetElasticPainter = () => {
      const elastic = chart.get(KEY)
      if (elastic) {
        elastic.reset()
      }
    }
  },

  /**
   * chart.render 中调用，hack convert point 方法
   * hack geom.getSnapRecords
   * @param chart
   */
  beforeGeomInit(chart) {
    const coord = chart.get('coord')
    if (!coord.isRect) return

    const elasticController = chart.get(KEY)
    if (!elasticController || !elasticController.isEnable()) return

    const [scaleX, scaleY] = elasticController.getScale()
    const [translateX, translateY] = elasticController.getTranslate()

    coord.convertPoint = function (point) {
      const self = this
      const { transposed } = self
      const xDim = transposed ? 'y' : 'x'
      const yDim = transposed ? 'x' : 'y'
      const { x, y } = self

      if (transposed) {
        return {
          x: x.start + ((x.end - x.start) * scaleX * point[xDim]) + translateX,
          y: y.start + ((y.end - y.start) * scaleY * point[yDim]) + translateY + ((y.end - y.start) * (1 - scaleY))
        }
      }

      return {
        x: x.start + ((x.end - x.start) * scaleX * point[xDim]) + translateX,
        y: y.start + ((y.end - y.start) * scaleY * point[yDim]) + translateY
      }
    }

    coord.invertPoint = function (point) {
      const self = this
      const { transposed } = self
      const xDim = transposed ? 'y' : 'x'
      const yDim = transposed ? 'x' : 'y'
      const { x, y } = self
      const rst = {}

      if (transposed) {
        rst[xDim] = (point.x - x.start - translateX) / ((x.end - x.start) * scaleX)
        rst[yDim] = (point.y - y.start - translateY - ((y.end - y.start) * (1 - scaleY))) / ((y.end - y.start) * scaleY)
      } else {
        rst[xDim] = (point.x - x.start - translateX) / ((x.end - x.start) * scaleX)
        rst[yDim] = (point.y - y.start - translateY) / ((y.end - y.start) * scaleY)
      }
      return rst
    }
  },

  beforeRenderAxis(chart) {
    const elasticController = chart.get(KEY)
    if (!elasticController || !elasticController.isEnable()) return

    // 给 axis 配置添加标识, 用于识别
    const axisController = chart.get('axisController')
    // if (axisController[FIELD_KEY]) return
    const { axisCfg } = axisController
    if (isObject(axisCfg)) {
      Object.entries(axisCfg).forEach(([key, cfg]) => {
        if (isObject(cfg)) {
          Object.entries(cfg).forEach(([type, typeConfig]) => {
            if (['line'].includes(type) && isObject(typeConfig)) {
              cfg[type] = {
                [FIELD_KEY]: key,
                ...typeConfig
              }
            }
          })
        }
      })
    }
    // axisController[FIELD_KEY] = true
  },

  /**
   * chart.render 中调用, 调整 grid/line/ticks 位置
   */
  afterGeomDraw(chart) {
    const coord = chart.get('coord')
    if (!coord.isRect) return

    const elasticController = chart.get(KEY)

    const axisController = chart.get('axisController')

    const { axes } = axisController || {}

    const { bottom, left, right } = axes || {}

    const xScale = chart.getXScale()
    const yScales = chart.getYScales();

    [bottom, left, right].forEach((axis) => {
      if (!axis || !axis.dimType || !axis._id) return
      const { dimType, index } = axis

      if (dimType === 'x') {
        axis[FIELD_KEY] = xScale.field
      }
      if (dimType === 'y') {
        axis[FIELD_KEY] = yScales[index] && yScales[index].field
      }
    })
    // 需要更新的坐标轴
    const needUpdateAxes = []

    const { x, y, transposed } = coord

    left && needUpdateAxes.push(left)
    right && needUpdateAxes.push(right)

    bottom && needUpdateAxes.push(bottom)
    // 区分需要更新哪种状态
    const type = elasticController.getMode()

    const currentAction = elasticController.getAction()

    if (!elasticController || !elasticController.isEnable()) {
      // 非详情页只需要调整 label 位置
      if (currentAction === null) {
        needUpdateAxes.forEach((axis) => {
          const { showAllLabel } = axis

          const { position, dimType } = axis
          const plot = getBasicPlot({ x, y, transposed }, dimType, position)

          // 缩放方向，对条形图默认为 y，在报告中未 enable 该插件，导致 type 不生效
          const activeType = transposed ? (type === 'x' ? 'y' : 'x') : type
          resetAxisLabel(axis, plot, activeType, transposed, !showAllLabel, showAllLabel ? {} : elasticController.getCaches())
        })
      }
      return
    }

    // action 为空时代表第一次初始化，需要计算 label 的位置
    updateAxes(chart, needUpdateAxes, elasticController.getCaches(), { x, y }, transposed, type, currentAction !== 'pan')

    // 设置 tooltip
    const tooltip = chart.get('tooltipController')
    if (tooltip) {
      const items = elasticController.getTooltipActiveItems()
      const initItem = elasticController.getTooltipInitItem()

      const item = Array.isArray(items) && items.length > 0 ? items[0].origin : initItem

      if (item) {
        const point = chart.getPosition(item)
        tooltip.showTooltip(point, true)
        elasticController.handleHighlightShape(point)
      }
    }
  },

  repaint(chart) {
    const elasticController = chart.get(KEY)
    if (!elasticController || !elasticController.isEnable()) return

    const tooltip = chart.get('tooltipController')
    if (!tooltip || !tooltip._lastActive) return

    elasticController.saveTooltipActive(tooltip._lastActive)
  },

  afterCanvasDestroyed(chart) {
    const elasticController = chart.get(KEY)
    elasticController && elasticController.destroy()
  },
}
